import { Link } from '@trashlab/sitesapp/src/organizations/redebins/components/shared/link';
import { PageSEO } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/page-seo';

export const HelpfulTipsContent = () => {
  return (
    <>
      <PageSEO pathname="/dumpster-rental-guide" />
      <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-8 italic text-center">
            Know What to Expect and How to Get the Most from Your RED-E-BIN Dumpster
          </h2>

          <div className="space-y-12">
            {/* Ordering Section */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Ordering
                </h3>
                <p className="text-[#333333]">
                  Things to keep in mind when scheduling delivery and pickup: You do not need to be present—just let us know where you'll want your RED-E-BIN Dumpster placed. Your rental period starts the day you receive the dumpster bin and ends on the 7th day for our 5YD/8x4x4 size or on the 10th day for our 10YD/10x5x5 size. If you would like to extend your time frame, arrange for an earlier pickup, or swap-out for a new dumpster bin, just log into your account to make the change, or get in touch with us directly!
                </p>
              </div>
            </div>

            {/* Ensure Space Section */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Ensure Space for Your RED-E-BIN Dumpster
                </h3>
                <p className="text-[#333333]">
                  Identify where you want your RED-E-BIN Dumpster (public streets generally are off-limits - please check with us before), and make sure the area for your dumpster is clear of any obstacles that could prevent proper placement, such as cars, trees or hanging wires. Be sure the size of your designated area can fit the bin and is located on your property.
                </p>
              </div>
            </div>

            {/* Anticipate Delivery Truck Needs */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Anticipate Delivery Truck Needs
                </h3>
                <p className="text-[#333333]">
                  Our bins are hauled by a pickup truck & trailer, so you won't need as much space as a traditional roll-off dumpster delivery. However, our delivery vehicle does need to be able to back into your driveway with the RED-E-BIN trailer attached. The driver will also need ample space to maneuver your RED-E-BIN Dumpster into position.
                </p>
              </div>
            </div>

            {/* Note Your Delivery Date */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Note Your Delivery Date
                </h3>
                <p className="text-[#333333] mb-4">
                  You choose your delivery date before the order is placed, as is the specific placement for your bin; be sure to leave detailed instructions in the provided field at checkout. Deliveries and pickups take place between 8am–5pm, Monday–Saturday.
                </p>
                <ul className="space-y-2 text-[#333333] mb-4 ml-6 list-disc">
                  <li>
                    <strong>We will do our best to pick up bins scheduled for Saturday, but in some cases it will default to the first non holiday work day.</strong>
                  </li>
                  <li>
                    <strong>If your bin pick up is scheduled for a Sunday, it will automatically default to the first non holiday work day.</strong>
                  </li>
                </ul>
                <p className="text-[#333333]">
                  We always try to accommodate. So if there's a specific time of day when you want your RED-E-BIN Dumpster delivered, please note this in the "delivery details" portion of your order. And if you have strict scheduling needs, just start a chat with us or give us a call at{' '}
                  <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                    (952) 931-2577
                  </a>{' '}
                  to make special arrangements.
                </p>
              </div>
            </div>

            {/* Filling Your Dumpster */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">5</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Filling Your Dumpster
                </h3>
                <p className="text-[#333333] mb-4">
                  Be aware of what debris you'll be placing in your RED-E-BIN Dumpster, as there are limitations as to what we can safely dispose of. Find out what can and cannot be put into your RED-E-BIN Dumpster{' '}
                  <Link href="/prohibited-items" className="text-[#E31837] hover:underline">
                    here
                  </Link>
                  .
                </p>
                <p className="text-[#333333] mb-4">
                  If you over-fill your dumpster bin, we might not be able to take it, plus you may incur additional fees.
                </p>
                <p className="text-[#333333]">
                  A RED-E-BIN Dumpster can't exceed a total of 3 tons of weight; to estimate the weight of concrete or shingles, check out our{' '}
                  <Link href="/debris-calculator" className="text-[#E31837] hover:underline">
                    debris calculator.
                  </Link>
                </p>
              </div>
            </div>

            {/* Pickup Process */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">6</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Pickup Process
                </h3>
                <p className="text-[#333333]">
                  On the day of your pickup, our pros will ensure that your RED-E-BIN Dumpster and the debris it contains is picked up quickly and quietly. If your 7-day or 10-day timeline results in a Sunday or a national holiday pickup, we will push your pickup to the next business day. To extend your timeline, swap out a full RED-E-BIN Dumpster, or if you'd like to make changes to your pickup date, please log into your account or{' '}
                  <Link href="/contact" className="text-[#E31837] hover:underline">
                    contact us
                  </Link>{' '}
                  directly.
                </p>
              </div>
            </div>

            {/* Taxes & Fees */}
            <div className="flex items-start gap-4">
              <div className="text-[#E31837] text-3xl font-bold flex-shrink-0">7</div>
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4 italic">
                  Taxes & Fees
                </h3>
                <p className="text-[#333333]">
                  Some counties and cities require RED-E-BINS to collect extra fees and taxes. Just start a chat or give us a call at{' '}
                  <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                    (952) 931-2577
                  </a>{' '}
                  if you need help understanding the fees in your area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

